import isString from 'lodash/isString';
import moment from 'moment-timezone';
import FEDateUtils from '@spothero/utils/date';
import SHGlobals from 'sh-globals';

const HUMAN_DATE_FORMAT = 'MMM D';
const HUMAN_DATE_MONTHLY_FORMAT = 'MMM D, YYYY';
const HUMAN_TIME_FORMAT = 'h:mma';

const splitTextDate = (date, delimeter) => {
    const d = date.split(delimeter);

    return new Date(d[2], d[0] - 1, d[1]);
};

/**
 * Utilities for working with dates.
 * @module DateUtils
 */
const DateUtils = {
    /**
     * Check whether input type of 'date' is supported in the current browser.
     * @static
     * @function supportsDateInput
     * @example
     * DateUtils.supportsDateInput();
     * @returns {Boolean} - Whether the input type is supported.
     */
    supportsDateInput() {
        if (window.shTestInfo.isRunningTest) { return false; }

        const fakeValue = 'not-a-date';
        const input = document.createElement('input');

        input.setAttribute('type', 'date');
        input.setAttribute('value', fakeValue);

        return (input.value !== fakeValue);
    },

    /**
     * Converts a desktop input date to the native mobile date format.
     * @static
     * @function convertToMobile
     * @param {String} date - The input value in the format MM/DD/YYYY.
     * @param {String} [delimeter='/'] - The delimeter that separates the parts of the date.
     * @example
     * DateUtils.convertToMobile('01/19/1983');
     * @returns {String} - The mobile formatted date.
     */
    convertToMobile(date, delimeter = '/') {
        if (!isString(date)) {
            throw new Error('The supplied date is not a string.');
        }

        if (date.indexOf(delimeter) !== -1) {
            return moment(splitTextDate(date, delimeter)).format(SHGlobals.MOBILE_DATE_FORMAT);
        } else {
            return date;
        }
    },

    /**
     * Returns human readable start and end datetime strings from moment objects.
     * @static
     * @function getHumanDateTimesAsStrings
     * @param {Object} data - The data to pass to the function.
     * @param {Moment} data.startDateTimeObj - The date as a moment instance.
     * @param {Moment} data.endDateTimeObj - The time as a moment instance.
     * @param {Boolean} data.isMonthly - Is startDateTimeObj a monthly datetime?
     * @example
     * DateUtils.getHumanDateTimesAsStrings({
     *     startDateTimeObj: moment(),
     *     endDateTimeObj: moment().add(7, 'hours').add(23, 'minutes')
     * });
     * @returns {Object} - Human readable strings returned for startDate, startTime, endDate and endTime
     */
    getHumanDateTimesAsStrings({startDateTimeObj, endDateTimeObj, isMonthly}) {
        if (!moment.isMoment(startDateTimeObj) || (!isMonthly && !moment.isMoment(endDateTimeObj))) {
            throw new Error('The datetimes supplied are not an instance of Moment.');
        }

        let startDateString = null;
        let startTimeString = null;
        let endDateString = null;
        let endTimeString = null;

        if (startDateTimeObj) {
            if (moment().startOf('day').diff(startDateTimeObj.clone().startOf('day')) === 0) {
                startDateString = 'Today';
                startTimeString = (!isMonthly) ? startDateTimeObj.format(HUMAN_TIME_FORMAT) : '';
            } else {
                if (isMonthly) {
                    startDateString = startDateTimeObj.format(HUMAN_DATE_MONTHLY_FORMAT);
                } else {
                    startDateString = startDateTimeObj.format(HUMAN_DATE_FORMAT);
                    startTimeString = startDateTimeObj.format(HUMAN_TIME_FORMAT);
                }
            }
        }

        if (!isMonthly && endDateTimeObj) {
            if (startDateTimeObj.clone().startOf('day').diff(endDateTimeObj.clone().startOf('day')) === 0) {
                if (moment().startOf('day').diff(endDateTimeObj.clone().startOf('day')) === 0) {
                    endDateString = 'Today';
                    endTimeString = endDateTimeObj.format(HUMAN_TIME_FORMAT);
                } else {
                    endDateString = endDateTimeObj.format(HUMAN_DATE_FORMAT);
                    endTimeString = endDateTimeObj.format(HUMAN_TIME_FORMAT);
                }
            } else {
                if (moment().clone().startOf('day').diff(endDateTimeObj.clone().startOf('day'), 'days') === -1) {
                    endDateString = 'Tmrw';
                    endTimeString = endDateTimeObj.format(HUMAN_TIME_FORMAT);
                } else {
                    endDateString = endDateTimeObj.format(HUMAN_DATE_FORMAT);
                    endTimeString = endDateTimeObj.format(HUMAN_TIME_FORMAT);
                }
            }
        }

        return {
            startDate: startDateString,
            startTime: startTimeString,
            endDate: endDateString,
            endTime: endTimeString
        };
    }
};

export default {
    ...FEDateUtils,
    ...DateUtils
};



// WEBPACK FOOTER //
// ./frontend/src/js/utils/date.js